// The bell's kinds, and a league's week folded into one row (DIRECTION A11,
// PLAN-2026-10-18-families-and-newcomers §6).
//
// A RULE A SECOND CLIENT NEEDS, so it lives here and not in the web app: which
// kind each notification is, what a kind is called, and which rows belong to
// one league's week. The store reads `groupOf` when a row is written and folds
// a group into one row when the list is read; a client sorts by `kindOf`.

import { z } from 'zod';
import type { NotificationTypeId } from './notifications';

/** The kinds the bell sorts into, in the order its chips show them. */
export const NOTIFICATION_KINDS = [
  'league',
  'team',
  'awards',
  'achievements',
  'chat',
  'watching',
] as const;
export const NotificationKind = z.enum(NOTIFICATION_KINDS);
export type NotificationKindId = (typeof NOTIFICATION_KINDS)[number];

/** What each kind is called, on a chip and as a heading. */
export const KIND_TITLES: Record<NotificationKindId, string> = {
  league: 'Your league',
  team: 'Your team',
  awards: 'Awards',
  achievements: 'Achievements',
  chat: 'Chat',
  watching: 'Players you watch',
};

const KIND_OF: Record<NotificationTypeId, NotificationKindId> = {
  'league.joined': 'league',
  'league.seat_opened': 'league',
  'league.removed': 'league',
  'league.rules_changed': 'league',
  'league.override': 'league',
  'league.reversal': 'league',
  'league.waivers': 'league',
  'league.vote_opened': 'league',
  'league.election_opened': 'league',
  'league.election_resolved': 'league',
  'league.job_failed': 'league',
  'league.stats_missing': 'league',
  'league.next_season': 'league',
  'seat.checkin': 'team',
  'draft.scheduled': 'league',
  'draft.reminder': 'team',
  'draft.plan_missing': 'team',
  'draft.on_the_clock': 'team',
  'draft.slow_turn': 'team',
  'draft.outbid': 'team',
  'draft.finished': 'league',
  'draft.failed': 'league',
  'lineup.problem': 'team',
  'lineup.starter_news': 'team',
  'lineup.nudge': 'team',
  'waivers.processed': 'team',
  'trade.offered': 'team',
  'trade.answered': 'team',
  'trade.withdrawn': 'team',
  'trade.review': 'league',
  'trade.processed': 'team',
  'trade.vetoed': 'team',
  'matchup.final': 'league',
  'recap.ready': 'league',
  'award.earned': 'awards',
  'stats.corrected': 'league',
  'playoffs.clinched': 'league',
  'playoffs.eliminated': 'league',
  'chat.mention': 'chat',
  'chat.message': 'chat',
  'watch.status': 'watching',
  'watch.dropped': 'watching',
  'achievement.unlocked': 'achievements',
  // Only an admin is sent one, and a chip of its own would sit on every
  // person's bell; it is about the leagues' weeks, so it sorts with them.
  'admin.stats_late': 'league',
};

/** The kind a notification type is. Every type has one; the record is exhaustive. */
export function kindOf(type: NotificationTypeId): NotificationKindId {
  return KIND_OF[type];
}

/** The types of a kind, for a store's filter. */
export function typesOf(kind: NotificationKindId): NotificationTypeId[] {
  return (Object.keys(KIND_OF) as NotificationTypeId[]).filter((t) => KIND_OF[t] === kind);
}

/** The rows a league's week folds together: its result, its awards and its recap. */
const WEEK_TYPES: readonly NotificationTypeId[] = ['matchup.final', 'award.earned', 'recap.ready'];

/**
 * The group a notification belongs to, written on its row: `week|<league>|<period>`
 * for a league's week's result, awards and recap, and null for anything else
 * (a season award, which has no week, among them).
 */
export function groupOf(event: {
  type: NotificationTypeId;
  leagueId: string | null;
  payload: Record<string, string | number | boolean | null>;
}): string | null {
  if (!WEEK_TYPES.includes(event.type) || event.leagueId === null) return null;
  const period = event.payload['period'];
  const n = typeof period === 'number' ? period : typeof period === 'string' ? Number(period) : NaN;
  if (!Number.isInteger(n) || n < 1) return null;
  return `week|${event.leagueId}|${n}`;
}
